// src/pages/purchase-requisition/components/OrderFormDialog.jsx
import React, { useState, useEffect } from 'react';
import {
    Typography, TextField, Button, Dialog, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, Stack, IconButton,
    AppBar, Toolbar, Slide, Divider, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Box, Checkbox, FormControlLabel
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";
import MaterialAutocomplete from './MaterialAutocomplete';
import MaterialDescriptionAutocomplete from './MaterialDescriptionAutocomplete';

/*
dari kode berikut ini, tambahkan pengkodean dengan logika sebagai berikut:
- pindahkan checkbox "tampilkan referensi" ke tabel bagian aksi, sehingga setiap tombol tambah item di 
- jika tampilkan referensi aktif dan memilih list dari Kode Material maka Deskripsi Material akan terisi secara otomatis, begitu juga sebaliknya.
- jika mode edit aktif maka tombol 
*/

// Transition component for full-screen dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function to format number as accounting (no decimals, with thousand separators)
const formatAccounting = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = parseFloat(String(value).replace(/[^0-9]/g, ''));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('id-ID', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
};

// Komponen untuk dialog form order utama
function OrderFormDialog({
    open,
    onClose,
    formValues,
    flowConfigurations,
    handleDeleteItemDetail,
    handleSubmitOrder,
    setSnackbar,
    isFlowConfigReadOnly,
    uomList,
    orderBeingEditedId,
    setFormValues
}) {
    const [newItems, setNewItems] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaveMode, setIsSaveMode] = useState(false);
    const [originalItems, setOriginalItems] = useState([]);
    //const [showReference, setShowReference] = useState(true);
    const [showReferenceForItems, setShowReferenceForItems] = useState({});

    // Fungsi untuk toggle referensi per item
const toggleReferenceForItem = (itemIndex, isNewItem = true) => {
  const key = isNewItem ? `new-${itemIndex}` : `existing-${itemIndex}`;
  setShowReferenceForItems(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
};

// Fungsi untuk mendapatkan status referensi per item
const getReferenceStatusForItem = (itemIndex, isNewItem = true) => {
  const key = isNewItem ? `new-${itemIndex}` : `existing-${itemIndex}`;
  // Default false untuk item baru, true untuk existing items jika diinginkan
  return showReferenceForItems[key] !== undefined ? showReferenceForItems[key] : false;
};

    const handleMainFormChange = (field, value) => {
        setFormValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

// tambahkan useEffect untuk menghitung total
useEffect(() => {
    const total = calculateTotal();
    setFormValues(prev => ({
        ...prev,
        total_amount: total
    }));
}, [formValues.item_details, newItems]);

    // Temukan nama alur yang dipilih untuk ditampilkan jika read-only
    const selectedFlowName = flowConfigurations.find(
        (flow) => flow.id === formValues.flow_config_id
    )?.flow_name || '';

    // Temukan nama UoM untuk ditampilkan di tabel
    const getUomName = (uomId) => {
        const uom = uomList.find(item => item.id === uomId);
        return uom ? uom.nama_uom : uomId;
    };

    // Fungsi untuk memeriksa apakah ada item material
    const hasItems = () => {
        return formValues.item_details.length > 0 || newItems.length > 0;
    };

    const handleAddNewItem = () => {
        if (newItems.length >= 10) {
            setSnackbar({ open: true, message: "Maksimal 10 item baru dapat ditambahkan sekaligus", severity: "warning" });
            return;
        }

        setNewItems(prev => [
            ...prev,
            {
                material_code: '',
                material_description: '',
                quantity_jul: '',
                unit_of_measure: '',
                valuation_price: '',
                total_amount: '',
                cost_center: '',
                notes: ''
            }
        ]);
        
        // Aktifkan mode edit dan simpan ketika menambahkan item baru
        setIsEditMode(true);
        setIsSaveMode(true);
    };

    const handleInputChange = (index, field, value) => {
        setNewItems(prev => {
            const updatedItems = [...prev];
            const updatedItem = {
                ...updatedItems[index],
                [field]: value
            };

            // Auto-calculate total amount when quantity or valuation price changes
            if (field === 'quantity_jul' || field === 'valuation_price') {
                const quantity = field === 'quantity_jul' ? parseFloat(value) : parseFloat(updatedItem.quantity_jul);
                const valuationPrice = field === 'valuation_price' ? parseFloat(String(value).replace(/[^0-9]/g, '')) : 
                                     parseFloat(String(updatedItem.valuation_price).replace(/[^0-9]/g, ''));
                
                if (!isNaN(quantity) && !isNaN(valuationPrice)) {
                    updatedItem.total_amount = (quantity * valuationPrice).toString();
                } else {
                    updatedItem.total_amount = '';
                }
            }

            updatedItems[index] = updatedItem;
            return updatedItems;
        });
    };

    const handleDeleteNewItem = (index) => {
        setNewItems(prev => prev.filter((_, i) => i !== index));
    };

    // Fungsi untuk mendeteksi perubahan pada item yang sudah ada
const hasExistingItemsChanges = () => {
    if (originalItems.length !== formValues.item_details.length) return true;
    
    return formValues.item_details.some((item, index) => {
        const originalItem = originalItems[index];
        if (!originalItem) return true;
        
        return item.material_code !== originalItem.material_code ||
               item.material_description !== originalItem.material_description ||
               item.quantity_jul !== originalItem.quantity_jul ||
               item.unit_of_measure !== originalItem.unit_of_measure ||
               item.valuation_price !== originalItem.valuation_price ||
               item.cost_center !== originalItem.cost_center ||
               item.notes !== originalItem.notes;
    });
};

const handleSaveAllItems = () => {
    // Validasi semua item baru - HANYA validasi field selain material
    const invalidItems = [];
    
    newItems.forEach((item, index) => {
        if (!item.quantity_jul || 
            !item.unit_of_measure || !item.valuation_price || !item.cost_center) {
            invalidItems.push(index + 1);
        }
    });

    if (invalidItems.length > 0) {
        setSnackbar({ 
            open: true, 
            message: `Item ${invalidItems.join(', ')} belum lengkap. Harap isi semua field yang wajib.`, 
            severity: "error" 
        });
        return;
    }

    // Calculate total amount untuk semua item
    const validItems = newItems.map(item => {
        const quantity = parseFloat(item.quantity_jul);
        const valuationPrice = parseFloat(String(item.valuation_price).replace(/[^0-9]/g, ''));
        const totalAmount = quantity * valuationPrice;

        return {
            ...item,
            total_amount: totalAmount.toString()
        };
    });

    // Gunakan setFormValues langsung untuk update state
    setFormValues(prev => ({
        ...prev,
        item_details: [...prev.item_details, ...validItems]
    }));

    setNewItems([]);
    setIsEditMode(false);
    setIsSaveMode(false);
    setSnackbar({ 
        open: true, 
        message: `${validItems.length} item berhasil ditambahkan`, 
        severity: "success" 
    });
};

// Check if all required fields are filled for all items
const areAllItemsValid = () => {
    // Check existing items
    const existingItemsValid = formValues.item_details.every(item => 
        item.material_code && item.material_description && item.quantity_jul && 
        item.unit_of_measure && item.valuation_price && item.cost_center
    );

    // Check new items
    const newItemsValid = newItems.every(item => {
        // Field wajib selain material
        const otherFieldsValid = item.quantity_jul && 
            item.unit_of_measure && item.valuation_price && item.cost_center;
        
        // Untuk material, kita tidak memvalidasi di sini karena
        // komponen Autocomplete sudah menangani validasinya sendiri
        return otherFieldsValid;
    });

    return existingItemsValid && newItemsValid;
};

const handleSubmit = () => {
    // Simpan semua item baru terlebih dahulu
    if (newItems.length > 0) {
        // Validasi semua item baru - HANYA validasi field selain material
        const invalidItems = [];
        
        newItems.forEach((item, index) => {
            if (!item.quantity_jul || 
                !item.unit_of_measure || !item.valuation_price || !item.cost_center) {
                invalidItems.push(index + 1);
            }
        });

        if (invalidItems.length > 0) {
            setSnackbar({ 
                open: true, 
                message: `Item ${invalidItems.join(', ')} belum lengkap. Harap isi semua field yang wajib.`, 
                severity: "error" 
            });
            return;
        }

        // Calculate total amount untuk semua item
        const validItems = newItems.map(item => {
            const quantity = parseFloat(item.quantity_jul);
            const valuationPrice = parseFloat(String(item.valuation_price).replace(/[^0-9]/g, ''));
            const totalAmount = quantity * valuationPrice;

            return {
                ...item,
                total_amount: totalAmount.toString()
            };
        });

        // Gunakan setFormValues langsung untuk update state
        setFormValues(prev => ({
            ...prev,
            item_details: [...prev.item_details, ...validItems]
        }));

        setNewItems([]);
        setIsEditMode(false);
        setIsSaveMode(false);
        // Setelah menyimpan item baru, lanjutkan submit
        setTimeout(() => {
            handleSubmitOrder();
        }, 0);
    } else {
        // Jika tidak ada item baru, langsung submit
        handleSubmitOrder();
    }
};

    const handleEditClick = () => {
        setIsEditMode(true);
        setIsSaveMode(true);
    };

    const handleSaveClick = () => {
        if (newItems.length > 0) {
            handleSaveAllItems();
        } else {
            setIsEditMode(false);
            setIsSaveMode(false);
        }
    };



// Fungsi untuk menghitung total keseluruhan
const calculateTotal = () => {
    let total = 0;
    
    // Hitung total dari item yang sudah ada
    formValues.item_details.forEach(item => {
        const amount = parseFloat(String(item.total_amount).replace(/[^0-9]/g, '')) || 0;
        total += amount;
    });
    
    // Hitung total dari item baru
    newItems.forEach(item => {
        const amount = parseFloat(String(item.total_amount).replace(/[^0-9]/g, '')) || 0;
        total += amount;
    });
    
    return total;
};

// Handler untuk perubahan judul
const handleOrderTitleChange = (e) => {
    setFormValues(prev => ({
        ...prev,
        order_title: e.target.value
    }));
};

    // Format total menjadi format accounting
    const formattedTotal = formatAccounting(calculateTotal());

// Handler untuk perubahan kode material dengan Autocomplete
// Ganti handler Material Code Change
const handleMaterialCodeChange = (index, value, isManualInput = false, isNewItem = true) => {
    if (isNewItem) {
        handleInputChange(index, 'material_code', value);
        
        // Jika input manual, reset deskripsi
        if (isManualInput) {
            handleInputChange(index, 'material_description', '');
        } else {
            // Cari deskripsi material yang sesuai
            const materialRef = ref(database, 'db_warehouse/db_ppic/db_config/tb_kode_material');
            onValue(materialRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const material = Object.values(data).find(
                        item => item.kode_material === value
                    );
                    if (material && material.deskripsi_material) {
                        handleInputChange(index, 'material_description', material.deskripsi_material);
                    }
                }
            }, { onlyOnce: true });
        }
    } else {
        // Untuk item yang sudah ada
        const updatedItems = [...formValues.item_details];
        updatedItems[index].material_code = value;
        
        // Jika input manual, reset deskripsi
        if (isManualInput) {
            updatedItems[index].material_description = '';
            setFormValues(prev => ({
                ...prev,
                item_details: updatedItems
            }));
        } else {
            // Cari deskripsi material yang sesuai
            const materialRef = ref(database, 'db_warehouse/db_ppic/db_config/tb_kode_material');
            onValue(materialRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const material = Object.values(data).find(
                        item => item.kode_material === value
                    );
                    if (material && material.deskripsi_material) {
                        updatedItems[index].material_description = material.deskripsi_material;
                    }
                }
                setFormValues(prev => ({
                    ...prev,
                    item_details: updatedItems
                }));
            }, { onlyOnce: true });
        }
    }
};

// Ganti handler Material Description Change
const handleMaterialDescriptionChange = (index, value, isManualInput = false, isNewItem = true) => {
    if (isNewItem) {
        handleInputChange(index, 'material_description', value);
        
        // Jika input manual, reset kode
        if (isManualInput) {
            handleInputChange(index, 'material_code', '');
        } else {
            // Cari kode material yang sesuai
            const materialRef = ref(database, 'db_warehouse/db_ppic/db_config/tb_kode_material');
            onValue(materialRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const material = Object.values(data).find(
                        item => item.deskripsi_material === value
                    );
                    if (material && material.kode_material) {
                        handleInputChange(index, 'material_code', material.kode_material);
                    }
                }
            }, { onlyOnce: true });
        }
    } else {
        // Untuk item yang sudah ada
        const updatedItems = [...formValues.item_details];
        updatedItems[index].material_description = value;
        
        // Jika input manual, reset kode
        if (isManualInput) {
            updatedItems[index].material_code = '';
            setFormValues(prev => ({
                ...prev,
                item_details: updatedItems
            }));
        } else {
            // Cari kode material yang sesuai
            const materialRef = ref(database, 'db_warehouse/db_ppic/db_config/tb_kode_material');
            onValue(materialRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const material = Object.values(data).find(
                        item => item.deskripsi_material === value
                    );
                    if (material && material.kode_material) {
                        updatedItems[index].material_code = material.kode_material;
                    }
                }
                setFormValues(prev => ({
                    ...prev,
                    item_details: updatedItems
                }));
            }, { onlyOnce: true });
        }
    }
};

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        {orderBeingEditedId ? "Edit Permintaan Order" : "Ajukan Permintaan Order Baru"}
                    </Typography>
                    
                    
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ p: 2 }}>
                {isFlowConfigReadOnly ? (
                    <TextField
                        margin="dense"
                        label="Alur Persetujuan Default"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={selectedFlowName}
                        InputProps={{
                            readOnly: true,
                        }}
                        disabled
                        sx={{ mb: 2 }}
                    />
                ) : (
                    <TextField
                        margin="dense"
                        label="Alur Persetujuan"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value="Tidak ada alur persetujuan default. Harap hubungi administrator."
                        InputProps={{
                            readOnly: true,
                        }}
                        disabled
                        sx={{ mb: 2 }}
                    />
                )}

                {/* Tambahkan input Judul Permintaan Order yang opsional */}
<TextField
    margin="dense"
    label="Masukkan Judul Permintaan Order (Opsional)"
    type="text"
    fullWidth
    variant="outlined"
    value={formValues.order_title || ''}
    onChange={handleOrderTitleChange} // Gunakan handler baru
    placeholder="Contoh: Permintaan Barang untuk Proyek XYZ"
    sx={{ mb: 2 }}
/>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Detail Item Material
                    </Typography>

                    <Box>

                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddNewItem}
                            disabled={newItems.length >= 10 || (formValues.item_details.length + newItems.length) >= 10}
                            >
                            Tambah Item ({(formValues.item_details.length + newItems.length)}/10)
                        </Button>
                    </Box>
                    
                </Box>

                <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Kode Material *</TableCell>
                                <TableCell>Deskripsi Material *</TableCell>
                                <TableCell>QTY *</TableCell>
                                <TableCell>UOM *</TableCell>
                                <TableCell>Valuation Price *</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Cost Center *</TableCell>
                                <TableCell>Noted Kebutuhan</TableCell>
                                <TableCell>Aksi</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Rows for adding new items */}
                            {newItems.map((item, index) => (
                                <TableRow key={`new-${index}`}>
                                    <TableCell>
{getReferenceStatusForItem(index, true) ? (
  <MaterialAutocomplete
    size="small"
    value={item.material_code}
    onChange={(e) => {
      handleMaterialCodeChange(index, e.target.value, e.isManualInput, true);
    }}
    placeholder="Kode Material"
    required
    error={!item.material_code}
    name="material_code"
    onDescriptionChange={(description) => 
      handleInputChange(index, 'material_description', description)
    }
    sx={{ width: 260 }}
    freeSolo={!getReferenceStatusForItem(index, true)} // Diubah di sini
  />
) : (
  <TextField
    size="small"
    value={item.material_code}
    onChange={(e) => handleInputChange(index, 'material_code', e.target.value)}
    placeholder="Kode Material"
    required
    error={!item.material_code}
    sx={{ width: 260 }}
  />
)}
                                    </TableCell>
                                    <TableCell>
{getReferenceStatusForItem(index, true) ? (
    <MaterialDescriptionAutocomplete
        size="small"
        value={item.material_description}
        onChange={(e) => {
            handleMaterialDescriptionChange(index, e.target.value, e.isManualInput, true);
        }}
        placeholder="Deskripsi Material"
        fullWidth
        required
        error={!item.material_description}
        name="material_description"
        onCodeChange={(code) => 
            handleInputChange(index, 'material_code', code)
        }
        sx={{ width: 310 }}
        freeSolo={!getReferenceStatusForItem(index, true)} // Diubah di sini
    />
) : (
    <TextField
        size="small"
        value={item.material_description}
        onChange={(e) => handleInputChange(index, 'material_description', e.target.value)}
        placeholder="Deskripsi Material"
        fullWidth
        required
        error={!item.material_description}
        sx={{ width: 310 }}
    />
)}
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.quantity_jul}
                                            onChange={(e) => handleInputChange(index, 'quantity_jul', e.target.value)}
                                            placeholder="QTY"
                                            sx={{ width: 80 }}
                                            required
                                            error={!item.quantity_jul}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <FormControl size="small" sx={{ minWidth: 124 }} error={!item.unit_of_measure}>
                                            <InputLabel>UOM</InputLabel>
                                            <Select
                                                value={item.unit_of_measure}
                                                onChange={(e) => handleInputChange(index, 'unit_of_measure', e.target.value)}
                                                label="UOM"
                                            >
                                                <MenuItem value=""><em>Pilih UOM</em></MenuItem>
                                                {uomList.map((uom) => (
                                                    <MenuItem key={uom.id} value={uom.id}>
                                                        {uom.nama_uom}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={formatAccounting(item.valuation_price)}
                                            onChange={(e) => handleInputChange(index, 'valuation_price', e.target.value)}
                                            placeholder="Harga"
                                            sx={{ width: 120 }}
                                            required
                                            error={!item.valuation_price}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={formatAccounting(item.total_amount)}
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                            disabled
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={item.cost_center}
                                            onChange={(e) => handleInputChange(index, 'cost_center', e.target.value)}
                                            placeholder="Cost Center"
                                            required
                                            error={!item.cost_center}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={item.notes}
                                            onChange={(e) => handleInputChange(index, 'notes', e.target.value)}
                                            placeholder="Noted"
                                        />
                                    </TableCell>
<TableCell align="center">
  <Box display="flex" justifyContent="center" alignItems="center">
    <FormControlLabel
      control={
        <Checkbox
          checked={getReferenceStatusForItem(index, true)}
          onChange={() => toggleReferenceForItem(index, true)}
          color="primary"
          size="small"
        />
      }
      label=""
    />
    <IconButton
      size="small"
      onClick={() => handleDeleteNewItem(index)}
    >
      <DeleteIcon />
    </IconButton>
  </Box>
</TableCell>
                                </TableRow>
                            ))}

                            {/* Existing items */}
                            {formValues.item_details.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>
{isEditMode ? (
    getReferenceStatusForItem(index, false) ? ( // Diubah di sini
        <MaterialAutocomplete
            size="small"
            value={item.material_code}
            onChange={(e) => {
                const updatedItems = [...formValues.item_details];
                updatedItems[index].material_code = e.target.value;
                setFormValues(prev => ({
                    ...prev,
                    item_details: updatedItems
                }));
            }}
            placeholder="Kode Material"
            required
            error={!item.material_code}
            freeSolo={!getReferenceStatusForItem(index, false)} // Diubah di sini
        />
    ) : (
        <TextField
            size="small"
            value={item.material_code}
            onChange={(e) => {
                const updatedItems = [...formValues.item_details];
                updatedItems[index].material_code = e.target.value;
                setFormValues(prev => ({
                    ...prev,
                    item_details: updatedItems
                }));
            }}
            placeholder="Kode Material"
            required
            error={!item.material_code}
        />
    )
) : (
    item.material_code
)}
                                    </TableCell>
                                    <TableCell>
{isEditMode ? (
    getReferenceStatusForItem(index, false) ? ( // Diubah di sini
        <MaterialDescriptionAutocomplete
            size="small"
            value={item.material_description}
            onChange={(e) => {
                const updatedItems = [...formValues.item_details];
                updatedItems[index].material_description = e.target.value;
                setFormValues(prev => ({
                    ...prev,
                    item_details: updatedItems
                }));
            }}
            placeholder="Deskripsi Material"
            fullWidth
            required
            error={!item.material_description}
            freeSolo={!getReferenceStatusForItem(index, false)} // Diubah di sini
        />
    ) : (
        <TextField
            size="small"
            value={item.material_description}
            onChange={(e) => {
                const updatedItems = [...formValues.item_details];
                updatedItems[index].material_description = e.target.value;
                setFormValues(prev => ({
                    ...prev,
                    item_details: updatedItems
                }));
            }}
            placeholder="Deskripsi Material"
            fullWidth
            required
            error={!item.material_description}
        />
    )
) : (
    item.material_description
)}
                                    </TableCell>
                                    <TableCell>
                                        {isEditMode ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={item.quantity_jul}
                                                onChange={(e) => {
                                                    const updatedItems = [...formValues.item_details];
                                                    updatedItems[index].quantity_jul = e.target.value;
                                                    
                                                    // Recalculate total amount
                                                    const quantity = parseFloat(e.target.value) || 0;
                                                    const valuationPrice = parseFloat(String(updatedItems[index].valuation_price).replace(/[^0-9]/g, '')) || 0;
                                                    updatedItems[index].total_amount = (quantity * valuationPrice).toString();
                                                    
                                                    setFormValues(prev => ({
                                                        ...prev,
                                                        item_details: updatedItems
                                                    }));
                                                }}
                                                placeholder="QTY"
                                                sx={{ width: 80 }}
                                                required
                                                error={!item.quantity_jul}
                                            />
                                        ) : (
                                            item.quantity_jul
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {isEditMode ? (
                                            <FormControl size="small" sx={{ minWidth: 100 }} error={!item.unit_of_measure}>
                                                <InputLabel>UOM</InputLabel>
                                                <Select
                                                    value={item.unit_of_measure}
                                                    onChange={(e) => {
                                                        const updatedItems = [...formValues.item_details];
                                                        updatedItems[index].unit_of_measure = e.target.value;
                                                        setFormValues(prev => ({
                                                            ...prev,
                                                            item_details: updatedItems
                                                        }));
                                                    }}
                                                    label="UOM"
                                                >
                                                    <MenuItem value=""><em>Pilih UOM</em></MenuItem>
                                                    {uomList.map((uom) => (
                                                        <MenuItem key={uom.id} value={uom.id}>
                                                            {uom.nama_uom}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            getUomName(item.unit_of_measure)
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {isEditMode ? (
                                            <TextField
                                                size="small"
                                                value={formatAccounting(item.valuation_price)}
                                                onChange={(e) => {
                                                    const updatedItems = [...formValues.item_details];
                                                    updatedItems[index].valuation_price = e.target.value;
                                                    
                                                    // Recalculate total amount
                                                    const quantity = parseFloat(updatedItems[index].quantity_jul) || 0;
                                                    const valuationPrice = parseFloat(String(e.target.value).replace(/[^0-9]/g, '')) || 0;
                                                    updatedItems[index].total_amount = (quantity * valuationPrice).toString();
                                                    
                                                    setFormValues(prev => ({
                                                        ...prev,
                                                        item_details: updatedItems
                                                    }));
                                                }}
                                                placeholder="Harga"
                                                sx={{ width: 120 }}
                                                required
                                                error={!item.valuation_price}
                                            />
                                        ) : (
                                            formatAccounting(item.valuation_price)
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatAccounting(item.total_amount)}
                                    </TableCell>
                                    <TableCell>
                                        {isEditMode ? (
                                            <TextField
                                                size="small"
                                                value={item.cost_center}
                                                onChange={(e) => {
                                                    const updatedItems = [...formValues.item_details];
                                                    updatedItems[index].cost_center = e.target.value;
                                                    setFormValues(prev => ({
                                                        ...prev,
                                                        item_details: updatedItems
                                                    }));
                                                }}
                                                placeholder="Cost Center"
                                                required
                                                error={!item.cost_center}
                                            />
                                        ) : (
                                            item.cost_center
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {isEditMode ? (
                                            <TextField
                                                size="small"
                                                value={item.notes}
                                                onChange={(e) => {
                                                    const updatedItems = [...formValues.item_details];
                                                    updatedItems[index].notes = e.target.value;
                                                    setFormValues(prev => ({
                                                        ...prev,
                                                        item_details: updatedItems
                                                    }));
                                                }}
                                                placeholder="Noted"
                                            />
                                        ) : (
                                            item.notes
                                        )}
                                    </TableCell>
                                    <TableCell>
  {isEditMode && (
    <FormControlLabel
      control={
        <Checkbox
          checked={getReferenceStatusForItem(index, false)}
          onChange={() => toggleReferenceForItem(index, false)}
          color="primary"
          size="small"
        />
      }
      label=""
    />
  )}
  <IconButton 
    size="small" 
    onClick={() => handleDeleteItemDetail(index)}
    
  >
    <DeleteIcon />
  </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}

{(formValues.item_details.length > 0 || newItems.length > 0) && (
    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
        <TableCell sx={{ fontWeight: 'bold' }}>
            Total Biaya:
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>
            {formattedTotal}
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
    </TableRow>
)}

                        </TableBody>
                    </Table>
                </TableContainer>

                {formValues.item_details.length === 0 && newItems.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        Belum ada item material ditambahkan.
                    </Typography>
                )}

{/* Tambahkan informasi catatan di sini */}
<Box sx={{ mt: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1, border: '1px solid #e0e0e0' }}>
  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
    Catatan:
  </Typography>
  <Typography variant="body2" paragraph sx={{ color: '#666', lineHeight: 1.6 }}>
    • Centang kotak di kolom Aksi untuk menampilkan referensi Kode dan Deskripsi Material.
  </Typography>
  <Typography variant="body2" paragraph sx={{ color: '#666', lineHeight: 1.6 }}>
    • Hilangkan centang jika material yang diajukan tidak ada dalam referensi.
  </Typography>
  <Typography variant="body2" paragraph sx={{ color: '#666', lineHeight: 1.6 }}>
    • Pastikan semua kolom berwarna merah sudah terisi dengan benar.
  </Typography>
  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
    • Setelah menekan tombol Simpan, jangan lupa klik tombol Ajukan Order agar permohonanmu bisa diproses oleh tim verifikasi.
  </Typography>
</Box>

            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="primary"
                    size="large"
                    sx={{ mr: 1 }}
                >
                    Batal
                </Button>
                
                {/* Tombol Edit - hanya muncul jika ada item */}
                {!isSaveMode && hasItems() && (
                    <Button
                        onClick={handleEditClick}
                        variant="outlined"
                        color="primary"
                        size="large"
                        startIcon={<EditIcon />}
                        sx={{ mr: 1 }}
                    >
                        Edit
                    </Button>
                )}
                
                {/* Tombol Simpan atau Ajukan Order - hanya muncul jika ada item */}
                {hasItems() && (
                    <>
                        {isSaveMode ? (
                            <Button
                                onClick={handleSaveClick}
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={!areAllItemsValid() || (newItems.length === 0 && !hasExistingItemsChanges())}
                            >
                                Simpan
                            </Button>
                        ) : (
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={!formValues.flow_config_id || 
                                         (formValues.item_details.length === 0 && newItems.length === 0) ||
                                         !areAllItemsValid()}
                            >
                                {orderBeingEditedId ? "Update Order" : "Ajukan Order"}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default OrderFormDialog;